import { useSyncExternalStore } from "react"

// Module-level "start the tour" request counter. The sidebar button increments
// it; GuideTour subscribes and (re)starts the tour when it changes. A counter
// (not a boolean) so repeated clicks restart the tour even if it is running.
const listeners = new Set<()=> void>()

let startRequestCount = 0

// The "don't auto-start again" flag is per-app, so one app's tour never
// silences another's: each tour passes its own id. All sortaballs variants
// (8-colors, 10-colors, …) share one gameplay tour and one key — they are the
// same game.
//
// Rule: the flag is set the moment a tour STARTS (not only when it is closed
// or finished), so a started tour is never auto-started again — a mid-tour
// reload or re-mount can't replay it. The sidebar "Start Tour" button force-
// starts regardless of the flag and never clears it.
export type TourId =
  | "guide"
  | "have-you-seen-it"
  | "reveal-it"
  | "select-correct-word"
  | "sortaballs"

export const TOUR_DISMISSED_KEYS: Record<TourId, string> = {
  // The generic shell tour keeps its original key so returning users keep
  // their existing dismissal.
  guide: "tour-guide-dismissed",
  "have-you-seen-it": "tour-guide-have-you-seen-it-dismissed",
  "reveal-it": "tour-guide-reveal-it-dismissed",
  "select-correct-word": "tour-guide-select-correct-word-dismissed",
  sortaballs: "tour-guide-sortaballs-dismissed",
}

export function requestTourStart() {
  startRequestCount += 1
  for (const listener of listeners) {
    listener()
  }
}

export function isTourDismissed(tourId: TourId): boolean {
  return localStorage.getItem(TOUR_DISMISSED_KEYS[tourId]) === "ON"
}

export function markTourDismissed(tourId: TourId): void {
  localStorage.setItem(TOUR_DISMISSED_KEYS[tourId], "ON")
}

export function useTourStartRequest() {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
}

function getSnapshot() {
  return startRequestCount
}

function subscribe(listener: ()=> void) {
  listeners.add(listener)
  return ()=> {
    listeners.delete(listener)
  }
}